import {Button} from '@material-ui/core';
import {Form, Formik} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router';
import {useAppDispatch} from '../../store';
import {LoadingSkeleton} from './LoadingSkeleton';
import cn from 'classnames';
import {OpenVXRuntimeContainer} from './OpenVXRuntimeContainer';
import {setONNXTask, TASK_STATUS} from '../TestDeployView/useDeploy';
import {ONNXRuntimeContainer} from '../ONNXRuntimeContainer/ONNXRuntimeContainer';
import './InferenceApp.scss';
import {getInferenceApplication} from './GetInferenceApplication';
import {
  Application,
  ApplicationResponse,
  SinglePageApplication,
} from '../../types/project/Application';
import {StreamingDevice} from '../../types/deployment/StreamingDeviceResponse';
import {useInferenceAppTask} from './useInferenceAppTask';
import {assertResponseBody} from '../../api/request';
import {getPublishedDeviceList} from '../../api/device/DeviceList';
import {getSubmitFunction, SubmitRequest} from './SubmitInferenceApplication';
import {InferenceAppExecution, InferenceAppForm} from './InferenceAppTypes';
import {BatchConfigPanel} from './BatchConfigPanel';
import {StreamConfigPanel} from './StreamConfigPanel';
import {OpenVXRuntimeStreamContainer} from './OpenVXRuntimeStreamContainer';
import {toast} from '../../base-components/StudioToast';
import Axios from 'axios';
import URL from '../../config/url';
import {NodeSource, ProductionNode} from '../../api/deployment/DeploymentNodes';
import {useEdgeInference} from './useEdgeInference';
import {Deployment} from '../../types/deployment/DeploymentResponse';
import {EdgeStreamContainer} from './EdgeStreamContainer';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../TestDeployView/HlsVideoStream';
import {useApplicationDetails} from '../../types/project/ApplicationDetails';
import {DeploymentStatusInfo} from '../../types/deployment/DeploymentStatusInfo';
import {fetchDeploymentStatusStateless} from '../../store/prodDeployments/dashboard';
import {REQUEST_POLLING_INTERVAL} from '../../config/constants';

export const ViewInferenceAppPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {projectId, applicationId, spaId} = useParams<{
    projectId: string;
    applicationId: string;
    spaId: string;
  }>();
  const [inProgress, setInProgress] = useState(false);
  const [executionContext, setExecutionContext] = useState<InferenceAppExecution>();
  const [deploymentNode, setDeploymentNode] = useState<ProductionNode>();
  const [streamingDevices, setStreamingDevices] = useState<StreamingDevice[]>([]);
  const [nodeSources, setNodeSources] = useState<NodeSource[]>([]);
  const [batchConfigFieldsReady, setBatchConfigFieldsReady] = useState(false);
  const [streamConfigFieldsReady, setStreamConfigFieldsReady] = useState(false);
  const [application, setApplication] = useState<Application>();
  const [streamSubject, setStreamSubject] = useState<VideoStreamSubject>();
  const applicationDetails = useApplicationDetails(applicationId);
  const [prodDeployment, setProdDeployment] = useState<Deployment>();
  const [deploymentStatusInfo, setDeploymentStatusInfo] = useState<
    DeploymentStatusInfo
  >();
  const {handleInferenceStart, handleInferenceStop} = useInferenceAppTask(projectId);
  const {
    handleEdgeInferenceStart,
    handleEdgeInferenceStop,
    fetchDeploymentNode,
  } = useEdgeInference(setProdDeployment);

  const [spa, setSpa] = useState<SinglePageApplication>();

  useEffect(() => {
    async function fetchApplication() {
      try {
        const {data} = await Axios.get<ApplicationResponse>(URL.APPLICATION_GET, {
          params: {applicationId},
        });
        const application = data?.body;
        if (application) {
          setApplication(application);
        }
      } catch (e) {
        console.error(e);
      }
    }
    fetchApplication();
  }, [applicationId]);

  useEffect(() => {
    async function fetchSpa() {
      const spaResponse = await getInferenceApplication(spaId, applicationId);
      if (!spaResponse) {
        console.log(intl.formatMessage({id: 'inferenceApp.error.notfound'}));
      } else {
        setSpa(spaResponse);
      }
    }
    fetchSpa();
  }, [spaId, applicationId, intl]);

  useEffect(() => {
    if (inProgress || !spa?.target?.deploymentId) return;

    const interval = setInterval(async () => {
      if (spa?.target?.deploymentId) {
        const deploymentStatusInfo = await fetchDeploymentStatusStateless(
          spa?.target?.deploymentId
        );
        if (deploymentStatusInfo) {
          setDeploymentStatusInfo(deploymentStatusInfo);
        }
      }
    }, REQUEST_POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, dispatch, inProgress, spa?.target?.deploymentId]);

  const disableButtonWithDeploymentStatus = useMemo(
    () =>
      ['RUNNING', 'DEPLOYING', 'STOPPING', 'STARTING'].includes(
        deploymentStatusInfo?.status || 'IDLE'
      ),
    [deploymentStatusInfo?.status]
  );

  useEffect(() => {
    const fetchStreamingDevicesList = async () => {
      const devices = await assertResponseBody(getPublishedDeviceList());
      if (devices) {
        setStreamingDevices(devices);
      }
    };

    fetchStreamingDevicesList();
    setStreamSubject(new VideoStreamSubject());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDeploymentNode(setDeploymentNode, setNodeSources, spa?.target.deploymentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spa]);

  const handleCancel = async (optId?: string, subject?: VideoStreamSubject) => {
    if (subject) {
      subject.removeListener('ViewSpaStreamObserver');
    }
    if (spa?.input.type === 'STREAM' && spa?.target.type === 'PRODUCTION') {
      handleEdgeInferenceStop(() => {
        setExecutionContext(undefined);
      });
    } else {
      handleInferenceStop(() => {
        setExecutionContext(undefined);
      }, optId);
    }
    setInProgress(false);
  };

  const handleSubmit = async (values: InferenceAppForm) => {
    if (!spa || !application || !streamSubject || !applicationDetails || inProgress) {
      return;
    }
    try {
      streamSubject.addListener('ViewSpaStreamObserver', new ViewSpaStreamObserver());
      setInProgress(true);

      const submitRequest: SubmitRequest = {
        spa,
        application,
        applicationDetails,
        configuredParameters: [],
        streamingDevices,
        streamSubject,
        values,
        handleInferenceStart,
        handleEdgeInferenceStart,
        setExecutionContext,
        onError: e => {
          handleStartError(e, spa);
        },
      };

      getSubmitFunction(submitRequest)(submitRequest);
    } catch (e) {
      console.log(' getSubmitFunction error');
      setInProgress(false);
      console.error(e, spa);
    }
  };

  const handleStartError = (e: Error, spa?: SinglePageApplication) => {
    if (inProgress && spa?.target.type === 'PRODUCTION') {
      handleCancel();
    } else {
      setInProgress(false);
      console.error(e);
    }
  };

  class ViewSpaStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.STREAM_FINISHED:
          handleCancel(event.deploymentId, event.subject);
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            toast.error(event.message);
          }
          handleCancel(event.deploymentId, event.subject);
          break;
        default:
          break;
      }
    }
  }

  return (
    <Formik<InferenceAppForm>
      initialValues={{
        name: '',
        exposeAPI: spa?.input.exposeAPI || false,
        exposeInferencePage: spa?.input.exposeInferencePage || false,
        postProcessorId: spa?.postProcessorId,
        images: [],
        runtimeMode: 'webgl',
        inferenceSource: 'IP_CAMERA',
        ipCamera: '',
        deviceCamera: '',
        schedulerId: undefined,
        postProcessorParams: [],
        preProcessorParams: [],
      }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({values}) => (
        <div className="inference-app">
          <div className="inference-app__menu">
            <h2>{application?.name}</h2>
          </div>
          <div className="inference-app__body">
            <Form className="inference-app__panel">
              {application && spa && (
                <>
                  {spa.input.type === 'BATCH' && (
                    <BatchConfigPanel
                      application={application}
                      inProgress={inProgress}
                      values={values}
                      spa={spa}
                      projectPostProcessors={[]}
                      viewMode={true}
                      setReady={setBatchConfigFieldsReady}
                    />
                  )}
                  {spa.input.type === 'STREAM' && (
                    <StreamConfigPanel
                      application={application}
                      inProgress={inProgress}
                      values={values}
                      spa={spa}
                      projectPostProcessors={[]}
                      streamingDevices={streamingDevices}
                      nodeSources={nodeSources}
                      viewMode={true}
                      setReady={setStreamConfigFieldsReady}
                    />
                  )}

                  <div className="inference-app__submit">
                    {disableButtonWithDeploymentStatus && (
                      <p>
                        <FormattedMessage id="inferenceApp.deviceInUse" />
                      </p>
                    )}
                    {inProgress && (
                      <Button
                        color="secondary"
                        variant="contained"
                        disableElevation
                        onClick={() => streamSubject?.raiseStreamFinishEvent()}
                      >
                        <FormattedMessage id="action.cancel" />
                      </Button>
                    )}
                    {!inProgress && (
                      <Button
                        color="primary"
                        variant="contained"
                        disableElevation
                        disabled={
                          inProgress ||
                          (spa?.target.deploymentId && !deploymentNode) ||
                          (spa?.input.type === 'BATCH' && !batchConfigFieldsReady) ||
                          (spa?.input.type === 'STREAM' && !streamConfigFieldsReady) ||
                          disableButtonWithDeploymentStatus
                        }
                        onClick={() => handleSubmit(values)}
                      >
                        <FormattedMessage id="inferenceApp.run" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Form>
            <section className="inference-app__results">
              <h5>
                {intl.formatMessage({
                  id:
                    inProgress && spa?.input.type !== 'STREAM'
                      ? 'form.loading'
                      : 'prodDeployment.inferenceResults',
                })}
              </h5>
              {inProgress ? (
                <LoadingSkeleton
                  count={values.images.length}
                  className="inference-app__results__loading-skeleton"
                />
              ) : null}
              <div
                id="inference-results-element"
                className={cn(inProgress && 'inference-app--hidden')}
              ></div>
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'OPENCV_PYTHON' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'BATCH' && (
                  <OpenVXRuntimeContainer
                    projectId={projectId}
                    application={executionContext.application}
                    spa={executionContext.spa}
                    images={executionContext.images}
                    inProgress={inProgress}
                    onError={message => {
                      if (message) {
                        toast.error(message);
                      }
                      setInProgress(false);
                    }}
                    onFinish={() => setInProgress(false)}
                  />
                )}
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'OPENCV_PYTHON' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'STREAM' &&
                executionContext.spa.target.type === 'TEST' &&
                executionContext.streamDeploymentId &&
                streamSubject && (
                  <OpenVXRuntimeStreamContainer
                    projectId={projectId}
                    deploymentId={executionContext.streamDeploymentId}
                    spaId={executionContext.spa.id}
                    spaPostProcessorId={executionContext.spa.postProcessorId}
                    displayRealtimeParameters={false}
                    streamSubject={streamSubject}
                  />
                )}
              {(executionContext?.type === 'OPENVX' ||
                executionContext?.type === 'OPENCV_PYTHON' ||
                executionContext?.type === 'PY_PICASSO') &&
                executionContext.spa.input.type === 'STREAM' &&
                executionContext.spa.target.type === 'PRODUCTION' &&
                executionContext.spa.target.deploymentId &&
                streamSubject && (
                  <>
                    {deploymentNode && prodDeployment && (
                      <EdgeStreamContainer
                        projectId={projectId}
                        deploymentId={prodDeployment.id}
                        streamSubject={streamSubject}
                      />
                    )}
                    {!deploymentNode && (
                      <FormattedMessage id="deployments.noTargetDevice" />
                    )}
                  </>
                )}
              {executionContext?.type === 'ONNX_RUNTIME_WEB' && (
                <ONNXRuntimeContainer
                  key={executionContext.id}
                  type={executionContext.inferenceSource}
                  projectId={projectId}
                  application={executionContext.application}
                  spa={executionContext.spa}
                  runtimeMode={executionContext.runtimeMode}
                  configuredParams={executionContext.configuredParams}
                  displayRealtimeParameters={false}
                  url={executionContext.onnxVideoURL}
                  files={executionContext.images}
                  inProgress={inProgress}
                  onError={message => {
                    if (message) {
                      toast.error(message);
                    }
                    setInProgress(false);
                    dispatch(setONNXTask({status: TASK_STATUS.IDLE}));
                  }}
                  onFinish={() => {
                    setInProgress(false);
                    dispatch(setONNXTask({status: TASK_STATUS.COMPLETE}));
                  }}
                />
              )}
            </section>
          </div>
        </div>
      )}
    </Formik>
  );
};
